import { useState } from 'react';
import { useAuthGuard } from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import client from '../api/client';

export default function Morpheme() {
  const checkAuth = useAuthGuard();
  const [urls, setUrls] = useState('');
  const [directText, setDirectText] = useState('');
  const [mode, setMode] = useState<'url' | 'text'>('url');
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);

  const handleAnalyze = async () => {
    if (!checkAuth()) return;
    setLoading(true);
    try {
      const urlList = urls.split('\n').map(u => u.trim()).filter(Boolean);
      const res = await client.post('/morpheme/analyze', {
        urls: mode === 'url' ? urlList : [],
        target_keywords: [],
        direct_text: mode === 'text' ? directText : null,
      });
      setResults(res.data.results);
    } catch { alert('분석 실패'); }
    finally { setLoading(false); }
  };

  const s: any = {
    card: { background: '#161B27', border: '0.5px solid #2A3045', borderRadius: 8, padding: 20, marginBottom: 12 },
    input: { width: '100%', background: '#0F1117', border: '0.5px solid #2A3045', borderRadius: 8, padding: '10px 14px', color: '#E8ECF4', fontSize: 13, boxSizing: 'border-box' },
    btn: { background: '#00D4AA', border: 'none', borderRadius: 8, padding: '10px 28px', color: '#0A1F18', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
    tab: (active: boolean) => ({ background: active ? '#1A2035' : 'none', border: active ? '0.5px solid #00D4AA' : '0.5px solid #2A3045', borderRadius: 6, padding: '7px 20px', color: active ? '#00D4AA' : '#8892A4', fontSize: 13, cursor: 'pointer', fontWeight: active ? 700 : 400 }),
    label: { color: '#B0BAC8', fontSize: 12, marginBottom: 6, display: 'block' },
  };

  const SentimentBar = ({ score }: { score: number }) => {
    const color = score >= 60 ? '#00D4AA' : score <= 40 ? '#F56565' : '#F6AD55';
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ flex: 1, background: '#0F1117', borderRadius: 4, height: 8, overflow: 'hidden' }}>
          <div style={{ width: `${score}%`, background: color, height: '100%', borderRadius: 4 }} />
        </div>
        <span style={{ color, fontSize: 13, fontWeight: 700, minWidth: 30 }}>{score}%</span>
      </div>
    );
  };

  return (
    <Layout>
      <div style={{ maxWidth: 900 }}>
        <div style={{ marginBottom: 20 }}>
          <div style={{ color: '#E8ECF4', fontSize: 18, fontWeight: 700 }}>형태소 분석기</div>
          <div style={{ color: '#8892A4', fontSize: 12, marginTop: 4 }}>카페·블로그 글을 분석해 글자수·단어 빈도·감성을 확인합니다</div>
        </div>

        <div style={s.card}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <button style={s.tab(mode === 'url')} onClick={() => setMode('url')}>URL 입력</button>
            <button style={s.tab(mode === 'text')} onClick={() => setMode('text')}>텍스트 직접 입력</button>
          </div>

          {mode === 'url' ? (
            <>
              <label style={s.label}>분석할 URL (줄바꿈으로 구분, 최대 10개)</label>
              <textarea style={{ ...s.input, height: 120, resize: 'vertical', marginBottom: 16 }}
                value={urls} onChange={e => setUrls(e.target.value)}
                placeholder={"https://blog.naver.com/...\nhttps://kin.naver.com/..."} />
            </>
          ) : (
            <>
              <label style={s.label}>분석할 텍스트 직접 입력</label>
              <textarea style={{ ...s.input, height: 160, resize: 'vertical', marginBottom: 16 }}
                value={directText} onChange={e => setDirectText(e.target.value)}
                placeholder="분석할 텍스트를 붙여넣기 하세요..." />
            </>
          )}

          <div style={{ color: '#8892A4', fontSize: 11, marginBottom: 12 }}>※ 신조어·브랜드명은 정상 추출되지 않을 수 있습니다</div>
          <button style={s.btn} onClick={handleAnalyze} disabled={loading}>
            {loading ? '분석 중...' : '분석 시작'}
          </button>
        </div>

        {results.map((r, i) => (
          <div key={i} style={s.card}>
            {r.error ? (
              <div>
                <div style={{ color: '#8892A4', fontSize: 12, marginBottom: 4, wordBreak: 'break-all' }}>{r.url}</div>
                <div style={{ color: '#F56565', fontSize: 13 }}>⚠️ {r.error}</div>
              </div>
            ) : (
              <>
                <div style={{ cursor: 'pointer' }} onClick={() => setExpanded(expanded === i ? null : i)}>
                  {r.title && <div style={{ color: '#E8ECF4', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{r.title}</div>}
                  <div style={{ color: '#8892A4', fontSize: 11, marginBottom: 6, wordBreak: 'break-all' }}>{r.url}</div>
                  {r.auto_keywords?.length > 0 && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                      {r.auto_keywords.map((kw: string) => (
                        <span key={kw} style={{ background: '#00D4AA22', border: '1px solid #00D4AA55', borderRadius: 4, padding: '2px 8px', color: '#00D4AA', fontSize: 11 }}>{kw}</span>
                      ))}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ color: '#4A5568', fontSize: 10 }}>글자수(공백포함)</div>
                      <div style={{ color: '#E8ECF4', fontSize: 16, fontWeight: 700 }}>{r.char_count_with_space?.toLocaleString()}</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ color: '#4A5568', fontSize: 10 }}>글자수(공백제외)</div>
                      <div style={{ color: '#E8ECF4', fontSize: 16, fontWeight: 700 }}>{r.char_count_without_space?.toLocaleString()}</div>
                    </div>
                    <div style={{ flex: 1, minWidth: 150 }}>
                      <div style={{ color: '#4A5568', fontSize: 10, marginBottom: 4 }}>감성 — {r.sentiment} (긍정 {r.pos_count} / 부정 {r.neg_count})</div>
                      <SentimentBar score={r.sentiment_score} />
                    </div>
                    <span style={{ color: '#4A5568', fontSize: 11 }}>{expanded === i ? '▲' : '▼'}</span>
                  </div>
                </div>

                {expanded === i && (
                  <div style={{ marginTop: 16 }}>
                    {r.keyword_counts && Object.keys(r.keyword_counts).length > 0 && (
                      <div style={{ marginBottom: 16 }}>
                        <div style={{ color: '#B0BAC8', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>🔍 키워드 포함 횟수</div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {Object.entries(r.keyword_counts).map(([kw, cnt]: any) => (
                            <div key={kw} style={{ background: '#0F1117', borderRadius: 6, padding: '6px 14px', textAlign: 'center' }}>
                              <div style={{ color: '#8892A4', fontSize: 11 }}>{kw}</div>
                              <div style={{ color: cnt > 0 ? '#00D4AA' : '#4A5568', fontSize: 16, fontWeight: 700 }}>{cnt}회</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div style={{ marginBottom: 16 }}>
                      <div style={{ color: '#B0BAC8', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>📊 자주 나온 단어 TOP 20</div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
                        {r.top_words?.map((w: any, wi: number) => {
                          const maxCount = r.top_words[0]?.count || 1;
                          const pct = Math.round((w.count / maxCount) * 100);
                          const color = wi === 0 ? '#00D4AA' : wi < 3 ? '#F6AD55' : '#8892A4';
                          return (
                            <div key={wi} style={{ background: '#0F1117', borderRadius: 6, padding: '8px 10px' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                <span style={{ color: '#E8ECF4', fontSize: 13, fontWeight: wi < 3 ? 700 : 400 }}>{w.word}</span>
                                <span style={{ color, fontSize: 13, fontWeight: 700 }}>{w.count}</span>
                              </div>
                              <div style={{ background: '#1E2535', borderRadius: 2, height: 4, overflow: 'hidden' }}>
                                <div style={{ width: `${pct}%`, background: color, height: '100%', borderRadius: 2 }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <div style={{ color: '#B0BAC8', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>📄 텍스트 미리보기</div>
                      <div style={{ background: '#0F1117', borderRadius: 6, padding: '12px 14px', color: '#8892A4', fontSize: 12, lineHeight: 1.7, maxHeight: 400, overflowY: 'auto' as const, whiteSpace: 'pre-wrap' as const }}>{r.text_preview}</div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        ))}
      </div>
    </Layout>
  );
}
